import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { ExcelTools } from "./DataTools";
import { AiButton, MoveCategory, NO_FILTERS, ShopFilters, ShowHide, applyFilters } from "./ShopFilters";
import { api, formatApiError } from "@/lib/api";
import { imgUrl } from "@/lib/content";
import { Empty, ImageField, Loading, PageHead, useAdmin } from "./ui";

const BLANK = { name: "", brand: "", category: "", size: "", price: null, description: "", instructions: "", stock: null, images: [], active: true, featured: false, sort: 0, fresha_url: "" };

const Field = ({ label, children }) => <label className="mu-field">{label}{children}</label>;


const ProductForm = ({ p, onDone }) => {
  const [f, setF] = useState({ ...BLANK, ...p });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, price: f.price === "" || f.price == null ? null : Number(f.price), stock: f.stock === "" || f.stock == null ? null : Number(f.stock), images: f.images.filter(Boolean) };
    delete body.sold_out;
    delete body.id; delete body.created_at; delete body.updated_at;
    try {
      if (p.id) await api.put(`/admin/products/${p.id}`, body); else await api.post("/admin/products", body);
      toast.success(p.id ? "Product saved" : "Product added");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form className="ad-card shop-form" onSubmit={save} data-testid="product-form">
      <h3>{p.id ? `Edit ${p.name}` : "New product"}</h3>
      <div className="shop-form-grid">
        <Field label="Name"><input required value={f.name} onChange={set("name")} data-testid="product-name-input" /></Field>
        <Field label="Brand"><input value={f.brand} onChange={set("brand")} data-testid="product-brand-input" /></Field>
        <Field label="Category"><input value={f.category} onChange={set("category")} placeholder="e.g. Serums" data-testid="product-category-input" /></Field>
        <Field label="Size"><input value={f.size} onChange={set("size")} placeholder="e.g. 30 mL" data-testid="product-size-input" /></Field>
        <Field label="Price (BSD) — leave blank to show “Price coming soon”">
          <input type="number" min="0" step="0.01" inputMode="decimal" value={f.price ?? ""} onChange={set("price")} data-testid="product-price-input" />
        </Field>
        <Field label="Stock — leave blank to not track; 0 shows “Sold out”">
          <input type="number" min="0" step="1" inputMode="numeric" value={f.stock ?? ""} onChange={set("stock")} data-testid="product-stock-input" />
        </Field>
        <Field label="Sort order"><input type="number" value={f.sort} onChange={(e) => setF({ ...f, sort: Number(e.target.value) })} data-testid="product-sort-input" /></Field>
      </div>
      <Field label="Description"><textarea rows={3} value={f.description} onChange={set("description")} data-testid="product-description-input" /></Field>
      <Field label="Instructions for use (shown as “How to use” on the product)"><textarea rows={3} value={f.instructions || ""} onChange={set("instructions")} data-testid="product-instructions-input" /></Field>
      <p className="mu-field">Photos (first one is the main photo)</p>
      {f.images.map((u, i) => (
        <div className="shop-img-row" key={i}>
          <ImageField value={u} onChange={(v) => setF({ ...f, images: f.images.map((x, j) => (j === i ? v : x)) })} testId={`product-image-${i}`} />
          <button type="button" className="ad-icon-btn" onClick={() => setF({ ...f, images: f.images.filter((_, j) => j !== i) })} aria-label="Remove photo" data-testid={`product-image-${i}-remove`}><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="btn light ad-add" onClick={() => setF({ ...f, images: [...f.images, ""] })} data-testid="product-add-image-button"><Plus size={14} /> Add photo</button>
      <label className="ad-check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="product-active-checkbox" /> Show in the shop</label>
      <div className="req-actions">
        <button className="btn gold" type="submit" disabled={busy} data-testid="product-save-button">{busy ? <Loader2 size={15} className="spin" /> : "Save product"}</button>
        <button className="btn light" type="button" onClick={onDone} data-testid="product-cancel-button">Cancel</button>
      </div>
    </form>
  );
};

const BulkBar = ({ data, list, sel, setSel, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const allOn = list.length > 0 && list.every((p) => sel.has(p.id));
  const toggleAll = () => setSel(allOn ? new Set([...sel].filter((id) => !list.some((p) => p.id === id))) : new Set([...sel, ...list.map((p) => p.id)]));
  const del = async () => {
    if (!window.confirm(`Delete ${sel.size} product${sel.size > 1 ? "s" : ""}? This can't be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-delete", { ids: [...sel] });
      toast.success(`${data.deleted} product${data.deleted === 1 ? "" : "s"} deleted`);
      setSel(new Set());
      onDeleted();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className={`shop-bulk ${sel.size ? "is-active" : ""}`} data-testid="product-bulk-bar">
      <label className="ad-check"><input type="checkbox" checked={allOn} onChange={toggleAll} data-testid="product-select-all" /> Select all{list.length ? ` (${list.length})` : ""}</label>
      {sel.size > 0 && <>
        <span className="shop-bulk-count" data-testid="product-selected-count">{sel.size} selected</span>
        <button type="button" className="btn light" onClick={() => setSel(new Set())} data-testid="product-clear-selection">Clear</button>
        <MoveCategory data={data} sel={sel} onMoved={onDeleted} />
        <ShowHide sel={sel} onDone={onDeleted} />
        <button type="button" className="btn shop-bulk-del" onClick={del} disabled={busy} data-testid="product-bulk-delete-button">
          {busy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Delete selected
        </button>
      </>}
    </div>
  );
};

export default function ShopEditor() {
  const { data } = useAdmin("products", "/admin/products");
  const [edit, setEdit] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sel, setSel] = useState(() => new Set());
  const qc = useQueryClient();
  const list = useMemo(() => applyFilters(data || [], filters), [data, filters]);
  const refresh = () => { setEdit(null); qc.invalidateQueries({ queryKey: ["admin", "products"] }); qc.invalidateQueries({ queryKey: ["products"] }); };
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); setSel((s) => { const n = new Set(s); n.delete(p.id); return n; }); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-shop">
      <PageHead eyebrow="Shop" title="Shop items">
        <AiButton task="categories" data={data} onDone={refresh} />
        <AiButton task="descriptions" data={data} onDone={refresh} />
        <AiButton task="directions" data={data} onDone={refresh} />
        <button className="btn gold" onClick={() => setEdit({})} data-testid="product-new-button"><Plus size={15} /> Add product</button>
      </PageHead>
      {edit ? <ProductForm p={edit} onDone={refresh} /> : (
        <>
          <ExcelTools testPrefix="shop" title="Update products with Excel" exportUrl="/admin/products/export" importUrl="/admin/products/import" filename="waxmoi-products.xlsx"
            text="Download the sheet (photo, name, description, price, stock, instructions for use), fill in the blanks, then upload it back. Only cells you fill in change — empty cells leave the current info as it is. To add or replace photos, paste a public image link (https://…, Google Drive and Dropbox share links work) in the “New image URL” or “Image” column — separate several links with a comma. Rows without a Product ID update the product with the same name, or are added as new products. Stock 0 shows the item as sold out."
            onImported={() => { qc.invalidateQueries({ queryKey: ["admin", "products"] }); qc.invalidateQueries({ queryKey: ["products"] }); }} />
          <ShopFilters data={data} f={filters} setF={setFilters} />
          <p className="muted-sm shop-count" data-testid="product-filter-count">Showing {list.length} of {data.length}</p>
          <BulkBar data={data} list={list} sel={sel} setSel={setSel} onDeleted={refresh} />
          {!list.length ? <Empty>No products found.</Empty> : (
            <div className="shop-admin-list">
              {list.map((p) => (
                <div className={`shop-admin-row ${p.active ? "" : "is-hidden"} ${sel.has(p.id) ? "is-selected" : ""}`} key={p.id} data-testid={`admin-product-${p.id}`}>
                  <input type="checkbox" className="shop-admin-check" checked={sel.has(p.id)} onChange={() => toggle(p.id)} aria-label={`Select ${p.name}`} data-testid="admin-product-select" />
                  {p.images[0] ? <img src={imgUrl(p.images[0], 120)} alt="" /> : <span className="shop-admin-noimg" />}
                  <button type="button" className="shop-admin-info" onClick={() => setEdit(p)} data-testid="admin-product-edit">
                    <small>{p.brand} · {p.category}</small>
                    <strong>{p.name}</strong>
                    <span>{p.price == null ? "No price yet" : `$${Number(p.price).toFixed(2)}`}{p.stock != null && <em className={`shop-stock ${p.sold_out ? "out" : ""}`} data-testid="admin-product-stock">{p.sold_out ? "Sold out" : `${p.stock} in stock`}</em>} {p.active ? <Eye size={12} /> : <EyeOff size={12} />}</span>
                  </button>
                  <button className="ad-icon-btn" onClick={() => remove(p)} aria-label="Delete product" data-testid="admin-product-delete"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
